import React, { useCallback, useMemo, useState } from 'react';
import CreateVoucherDialog from '@/features/vouchers/components/create-voucher-dialog/create-voucher-dialog';
import UpdateVoucherDialog, {
  type UpdateVoucerDialogProps,
} from '@/features/vouchers/components/update-voucher-dialog/update-voucher-dialog';
import DeleteVoucherDialog, {
  type DeleteVoucerDialogProps,
} from '@/features/vouchers/components/delete-voucher-dialog/delete-voucher-dialog';
import DetailVoucherDialog, {
  type DetailVoucherDialogProps,
} from '@/features/vouchers/components/detail-voucher-dialog/detail-voucher-dialog';
import DetailUserDialog, {
  type DetailUserDialogProps,
} from '@/features/users/components/detail-user-dialog/detail-user-dialog';
import EditUserDialog, {
  type EditUserDialogProps,
} from '@/features/users/components/edit-user-dialog/edit-user-dialog';
import DeleteUserDialog, {
  type DeleteUserDialogProps,
} from '@/features/users/components/delete-user-dialog/delete-user-dialog';
import DetailOrderDialog, {
  type DetailOrderDialogProps,
} from '@/features/orders/components/detail-order-dialog/detail-order-dialog';
import SetOrderPaidDialog, {
  type SetOrderPaidDialogProps,
} from '@/features/orders/components/set-order-paid-dialog/set-order-paid-dialog';
import DetailWithdrawDialog, {
  type DetailWithdrawDialogProps,
} from '@/features/withdraws/components/detail-withdraw-dialog/detail-withdraw-dialog';
import UploadTransferProofDialog, {
  type UploadTransferProofDialogProps,
} from '@/features/withdraws/components/upload-transfer-proof-dialog/upload-transfer-proof-dialog';
import CancelWithdrawDialog, {
  type CancelWithdrawDialogProps,
} from '@/features/withdraws/components/cancel-withdraw-dialog/cancel-withdraw-dialog';
import DetailSessionDialog, {
  type DetailSessionDialogProps,
} from '@/features/sessions/components/detail-session-dialog/detail-session-dialog';
import DeleteProductDialog, {
  type DeleteProductDialogProps,
} from '@/features/products/components/delete-product-dialog/delete-product-dialog';
import PublishProductDialog, {
  type PublishProductDialogProps,
} from '@/features/products/components/publish-product-dialog/publish-product-dialog';
import ArchiveProductDialog, {
  type ArchiveProductDialogProps,
} from '@/features/products/components/archive-product-dialog/archive-product-dialog';
import PrivateProductDialog, {
  type PrivateProductDialogProps,
} from '@/features/products/components/private-product-dialog/private-product-dialog';
import DeleteQuestionBankDialog, {
  type DeleteQuestionBankDialogProps,
} from '@/features/question-banks/components/delete-question-bank-dialog/delete-question-bank-dialog';
import CreateProductClaimDialog, {
  type CreateProductClaimDialogProps,
} from '@/features/product-claim/components/create-product-claim-dialog/create-product-claim-dialog';
import UpdateProductClaimDialog, {
  type UpdateProductClaimDialogProps,
} from '@/features/product-claim/components/update-product-claim-dialog/update-product-claim-dialog';
import DeleteProductClaimDialog, {
  type DeleteProductClaimDialogProps,
} from '@/features/product-claim/components/delete-product-claim-dialog/delete-product-claim-dialog';
import CreateAdminDialog from '@/features/admins/components/create-admin-dialog/create-admin-dialog';
import DetailAdminDialog, {
  type DetailAdminDialogProps,
} from '@/features/admins/components/detail-admin-dialog/detail-admin-dialog';
import EditAdminDialog, {
  type EditAdminDialogProps,
} from '@/features/admins/components/edit-admin-dialog/edit-admin-dialog';
import DeleteAdminDialog, {
  type DeleteAdminDialogProps,
} from '@/features/admins/components/delete-admin-dialog/delete-admin-dialog';
import ChangeAdminPasswordDialog, {
  type ChangeAdminPasswordDialogProps,
} from '@/features/admins/components/change-admin-password-dialog/change-admin-password-dialog';
import CreateRoleDialog from '@/features/roles/components/create-role-dialog/create-role-dialog';
import DetailRoleDialog, {
  type DetailRoleDialogProps,
} from '@/features/roles/components/detail-role-dialog/detail-role-dialog';
import EditRoleDialog, {
  type EditRoleDialogProps,
} from '@/features/roles/components/edit-role-dialog/edit-role-dialog';
import DeleteRoleDialog, {
  type DeleteRoleDialogProps,
} from '@/features/roles/components/delete-role-dialog/delete-role-dialog';
import DetailNotificationDeliveryDialog, {
  type DetailNotificationDeliveryDialogProps,
} from '@/features/notification-deliveries/components/detail-notification-delivery-dialog/detail-notification-delivery-dialog';
import { V2dialogContext, type V2DialogContext } from './v2-dialog-context';

export default function V2DialogProvider({
  children,
}: React.PropsWithChildren) {
  //  Create Voucher Dialog
  const [isOpenCreateVoucherDialog, setIsOpenCreateVoucherDialog] =
    useState<boolean>(false);
  const openCreateVoucherDialog: V2DialogContext['openCreateVoucherDialog'] =
    useCallback(() => {
      setIsOpenCreateVoucherDialog(true);
    }, []);
  const closeCreateVoucherDialog: V2DialogContext['closeCreateVoucherDialog'] =
    useCallback(() => {
      setIsOpenCreateVoucherDialog(false);
    }, []);

  //  Update Voucher Dialog
  const [isOpenUpdateVoucherDialog, setIsOpenUpdateVoucherDialog] =
    useState<boolean>(false);
  const [updateVoucherDialogProps, setUpdateVoucherDialogProps] =
    useState<Omit<UpdateVoucerDialogProps, 'dialog'>>();

  const openUpdateVoucherDialog: V2DialogContext['openUpdateVoucherDialog'] =
    useCallback(props => {
      setUpdateVoucherDialogProps(props);
      setIsOpenUpdateVoucherDialog(true);
    }, []);

  const closeUpdateVoucherDialog: V2DialogContext['closeUpdateVoucherDialog'] =
    useCallback(() => {
      setUpdateVoucherDialogProps(undefined);
      setIsOpenUpdateVoucherDialog(false);
    }, []);

  //  Delete Voucher Dialog
  const [isOpenDeleteVoucherDialog, setIsOpenDeleteVoucherDialog] =
    useState<boolean>(false);
  const [deleteVoucherDialogProps, setDeleteVoucherDialogProps] =
    useState<Omit<DeleteVoucerDialogProps, 'dialog'>>();

  const openDeleteVoucherDialog: V2DialogContext['openDeleteVoucherDialog'] =
    useCallback(props => {
      setDeleteVoucherDialogProps(props);
      setIsOpenDeleteVoucherDialog(true);
    }, []);

  const closeDeleteVoucherDialog: V2DialogContext['closeDeleteVoucherDialog'] =
    useCallback(() => {
      setDeleteVoucherDialogProps(undefined);
      setIsOpenDeleteVoucherDialog(false);
    }, []);

  //  Detail Voucher Dialog
  const [isOpenDetailVoucherDialog, setIsOpenDetailVoucherDialog] =
    useState<boolean>(false);
  const [detailVoucherDialogProps, setDetailVoucherDialogProps] =
    useState<Omit<DetailVoucherDialogProps, 'dialog'>>();

  const openDetailVoucherDialog: V2DialogContext['openDetailVoucherDialog'] =
    useCallback(props => {
      setDetailVoucherDialogProps(props);
      setIsOpenDetailVoucherDialog(true);
    }, []);

  const closeDetailVoucherDialog: V2DialogContext['closeDetailVoucherDialog'] =
    useCallback(() => {
      setDetailVoucherDialogProps(undefined);
      setIsOpenDetailVoucherDialog(false);
    }, []);

  //  Detail User Dialog
  const [isOpenDetailUserDialog, setIsOpenDetailUserDialog] =
    useState<boolean>(false);
  const [detailUserDialogProps, setDetailUserDialogProps] =
    useState<Omit<DetailUserDialogProps, 'dialog'>>();

  const openDetailUserDialog: V2DialogContext['openDetailUserDialog'] =
    useCallback(props => {
      setDetailUserDialogProps(props);
      setIsOpenDetailUserDialog(true);
    }, []);

  const closeDetailUserDialog: V2DialogContext['closeDetailUserDialog'] =
    useCallback(() => {
      setDetailUserDialogProps(undefined);
      setIsOpenDetailUserDialog(false);
    }, []);

  //  Edit User Dialog
  const [isOpenEditUserDialog, setIsOpenEditUserDialog] =
    useState<boolean>(false);
  const [editUserDialogProps, setEditUserDialogProps] =
    useState<Omit<EditUserDialogProps, 'dialog'>>();

  const openEditUserDialog: V2DialogContext['openEditUserDialog'] = useCallback(
    props => {
      setEditUserDialogProps(props);
      setIsOpenEditUserDialog(true);
    },
    []
  );

  const closeEditUserDialog: V2DialogContext['closeEditUserDialog'] =
    useCallback(() => {
      setEditUserDialogProps(undefined);
      setIsOpenEditUserDialog(false);
    }, []);

  //  Delete User Dialog
  const [isOpenDeleteUserDialog, setIsOpenDeleteUserDialog] =
    useState<boolean>(false);
  const [deleteUserDialogProps, setDeleteUserDialogProps] =
    useState<Omit<DeleteUserDialogProps, 'dialog'>>();

  const openDeleteUserDialog: V2DialogContext['openDeleteUserDialog'] =
    useCallback(props => {
      setDeleteUserDialogProps(props);
      setIsOpenDeleteUserDialog(true);
    }, []);

  const closeDeleteUserDialog: V2DialogContext['closeDeleteUserDialog'] =
    useCallback(() => {
      setDeleteUserDialogProps(undefined);
      setIsOpenDeleteUserDialog(false);
    }, []);

  //  Detail Order Dialog
  const [isOpenDetailOrderDialog, setIsOpenDetailOrderDialog] =
    useState<boolean>(false);
  const [detailOrderDialogProps, setDetailOrderDialogProps] =
    useState<Omit<DetailOrderDialogProps, 'dialog'>>();

  const openDetailOrderDialog: V2DialogContext['openDetailOrderDialog'] =
    useCallback(props => {
      setDetailOrderDialogProps(props);
      setIsOpenDetailOrderDialog(true);
    }, []);

  const closeDetailOrderDialog: V2DialogContext['closeDetailOrderDialog'] =
    useCallback(() => {
      setDetailOrderDialogProps(undefined);
      setIsOpenDetailOrderDialog(false);
    }, []);

  //  Set Order Paid Dialog
  const [isOpenSetOrderPaidDialog, setIsOpenSetOrderPaidDialog] =
    useState<boolean>(false);
  const [setOrderPaidDialogProps, setSetOrderPaidDialogProps] =
    useState<Omit<SetOrderPaidDialogProps, 'dialog'>>();

  const openSetOrderPaidDialog: V2DialogContext['openSetOrderPaidDialog'] =
    useCallback(props => {
      setSetOrderPaidDialogProps(props);
      setIsOpenSetOrderPaidDialog(true);
    }, []);

  const closeSetOrderPaidDialog: V2DialogContext['closeSetOrderPaidDialog'] =
    useCallback(() => {
      setSetOrderPaidDialogProps(undefined);
      setIsOpenSetOrderPaidDialog(false);
    }, []);

  //  Detail Withdraw Dialog
  const [isOpenDetailWithdrawDialog, setIsOpenDetailWithdrawDialog] =
    useState<boolean>(false);
  const [detailWithdrawDialogProps, setDetailWithdrawDialogProps] =
    useState<Omit<DetailWithdrawDialogProps, 'dialog'>>();

  const openDetailWithdrawDialog: V2DialogContext['openDetailWithdrawDialog'] =
    useCallback(props => {
      setDetailWithdrawDialogProps(props);
      setIsOpenDetailWithdrawDialog(true);
    }, []);

  const closeDetailWithdrawDialog: V2DialogContext['closeDetailWithdrawDialog'] =
    useCallback(() => {
      setDetailWithdrawDialogProps(undefined);
      setIsOpenDetailWithdrawDialog(false);
    }, []);

  //  Upload Transfer Proof Dialog
  const [isOpenUploadTransferProofDialog, setIsOpenUploadTransferProofDialog] =
    useState<boolean>(false);
  const [uploadTransferProofDialogProps, setUploadTransferProofDialogProps] =
    useState<Omit<UploadTransferProofDialogProps, 'dialog'>>();

  const openUploadTransferProofDialog: V2DialogContext['openUploadTransferProofDialog'] =
    useCallback(props => {
      setUploadTransferProofDialogProps(props);
      setIsOpenUploadTransferProofDialog(true);
    }, []);

  const closeUploadTransferProofDialog: V2DialogContext['closeUploadTransferProofDialog'] =
    useCallback(() => {
      setUploadTransferProofDialogProps(undefined);
      setIsOpenUploadTransferProofDialog(false);
    }, []);

  //  Cancel Withdraw Dialog
  const [isOpenCancelWithdrawDialog, setIsOpenCancelWithdrawDialog] =
    useState<boolean>(false);
  const [cancelWithdrawDialogProps, setCancelWithdrawDialogProps] =
    useState<Omit<CancelWithdrawDialogProps, 'dialog'>>();

  const openCancelWithdrawDialog: V2DialogContext['openCancelWithdrawDialog'] =
    useCallback(props => {
      setCancelWithdrawDialogProps(props);
      setIsOpenCancelWithdrawDialog(true);
    }, []);

  const closeCancelWithdrawDialog: V2DialogContext['closeCancelWithdrawDialog'] =
    useCallback(() => {
      setCancelWithdrawDialogProps(undefined);
      setIsOpenCancelWithdrawDialog(false);
    }, []);

  //  Detail Session Dialog
  const [isOpenDetailSessionDialog, setIsOpenDetailSessionDialog] =
    useState<boolean>(false);
  const [detailSessionDialogProps, setDetailSessionDialogProps] =
    useState<Omit<DetailSessionDialogProps, 'dialog'>>();

  const openDetailSessionDialog: V2DialogContext['openDetailSessionDialog'] =
    useCallback(props => {
      setDetailSessionDialogProps(props);
      setIsOpenDetailSessionDialog(true);
    }, []);

  const closeDetailSessionDialog: V2DialogContext['closeDetailSessionDialog'] =
    useCallback(() => {
      setDetailSessionDialogProps(undefined);
      setIsOpenDetailSessionDialog(false);
    }, []);

  //  Delete Product Dialog
  const [isOpenDeleteProductDialog, setIsOpenDeleteProductDialog] =
    useState<boolean>(false);
  const [deleteProductDialogProps, setDeleteProductDialogProps] =
    useState<Omit<DeleteProductDialogProps, 'dialog'>>();

  const openDeleteProductDialog: V2DialogContext['openDeleteProductDialog'] =
    useCallback(props => {
      setDeleteProductDialogProps(props);
      setIsOpenDeleteProductDialog(true);
    }, []);

  const closeDeleteProductDialog: V2DialogContext['closeDeleteProductDialog'] =
    useCallback(() => {
      setDeleteProductDialogProps(undefined);
      setIsOpenDeleteProductDialog(false);
    }, []);

  //  Publish Product Dialog
  const [isOpenPublishProductDialog, setIsOpenPublishProductDialog] =
    useState<boolean>(false);
  const [publishProductDialogProps, setPublishProductDialogProps] =
    useState<Omit<PublishProductDialogProps, 'dialog'>>();

  const openPublishProductDialog: V2DialogContext['openPublishProductDialog'] =
    useCallback(props => {
      setPublishProductDialogProps(props);
      setIsOpenPublishProductDialog(true);
    }, []);

  const closePublishProductDialog: V2DialogContext['closePublishProductDialog'] =
    useCallback(() => {
      setPublishProductDialogProps(undefined);
      setIsOpenPublishProductDialog(false);
    }, []);

  //  Archive Product Dialog
  const [isOpenArchiveProductDialog, setIsOpenArchiveProductDialog] =
    useState<boolean>(false);
  const [archiveProductDialogProps, setArchiveProductDialogProps] =
    useState<Omit<ArchiveProductDialogProps, 'dialog'>>();

  const openArchiveProductDialog: V2DialogContext['openArchiveProductDialog'] =
    useCallback(props => {
      setArchiveProductDialogProps(props);
      setIsOpenArchiveProductDialog(true);
    }, []);

  const closeArchiveProductDialog: V2DialogContext['closeArchiveProductDialog'] =
    useCallback(() => {
      setArchiveProductDialogProps(undefined);
      setIsOpenArchiveProductDialog(false);
    }, []);

  //  Private Product Dialog
  const [isOpenPrivateProductDialog, setIsOpenPrivateProductDialog] =
    useState<boolean>(false);
  const [privateProductDialogProps, setPrivateProductDialogProps] =
    useState<Omit<PrivateProductDialogProps, 'dialog'>>();

  const openPrivateProductDialog: V2DialogContext['openPrivateProductDialog'] =
    useCallback(props => {
      setPrivateProductDialogProps(props);
      setIsOpenPrivateProductDialog(true);
    }, []);

  const closePrivateProductDialog: V2DialogContext['closePrivateProductDialog'] =
    useCallback(() => {
      setPrivateProductDialogProps(undefined);
      setIsOpenPrivateProductDialog(false);
    }, []);

  //  Delete Question Bank Dialog
  const [isOpenDeleteQuestionBankDialog, setIsOpenDeleteQuestionBankDialog] =
    useState<boolean>(false);
  const [deleteQuestionBankDialogProps, setDeleteQuestionBankDialogProps] =
    useState<Omit<DeleteQuestionBankDialogProps, 'dialog'>>();

  const openDeleteQuestionBankDialog: V2DialogContext['openDeleteQuestionBankDialog'] =
    useCallback(props => {
      setDeleteQuestionBankDialogProps(props);
      setIsOpenDeleteQuestionBankDialog(true);
    }, []);

  const closeDeleteQuestionBankDialog: V2DialogContext['closeDeleteQuestionBankDialog'] =
    useCallback(() => {
      setDeleteQuestionBankDialogProps(undefined);
      setIsOpenDeleteQuestionBankDialog(false);
    }, []);

  //  Create Klaim Produk Dialog
  const [isOpenCreateProductClaimDialog, setIsOpenCreateProductClaimDialog] =
    useState<boolean>(false);
  const openCreateProductClaimDialog: V2DialogContext['openCreateProductClaimDialog'] =
    useCallback(() => {
      setIsOpenCreateProductClaimDialog(true);
    }, []);
  const closeCreateProductClaimDialog: V2DialogContext['closeCreateProductClaimDialog'] =
    useCallback(() => {
      setIsOpenCreateProductClaimDialog(false);
    }, []);

  //  Update Klaim Produk Dialog
  const [isOpenUpdateProductClaimDialog, setIsOpenUpdateProductClaimDialog] =
    useState<boolean>(false);
  const [updateProductClaimDialogProps, setUpdateProductClaimDialogProps] =
    useState<Omit<UpdateProductClaimDialogProps, 'dialog'>>();

  const openUpdateProductClaimDialog: V2DialogContext['openUpdateProductClaimDialog'] =
    useCallback(props => {
      setUpdateProductClaimDialogProps(props);
      setIsOpenUpdateProductClaimDialog(true);
    }, []);

  const closeUpdateProductClaimDialog: V2DialogContext['closeUpdateProductClaimDialog'] =
    useCallback(() => {
      setUpdateProductClaimDialogProps(undefined);
      setIsOpenUpdateProductClaimDialog(false);
    }, []);

  //  Delete Klaim Produk Dialog
  const [isOpenDeleteProductClaimDialog, setIsOpenDeleteProductClaimDialog] =
    useState<boolean>(false);
  const [deleteProductClaimDialogProps, setDeleteProductClaimDialogProps] =
    useState<Omit<DeleteProductClaimDialogProps, 'dialog'>>();

  const openDeleteProductClaimDialog: V2DialogContext['openDeleteProductClaimDialog'] =
    useCallback(props => {
      setDeleteProductClaimDialogProps(props);
      setIsOpenDeleteProductClaimDialog(true);
    }, []);

  const closeDeleteProductClaimDialog: V2DialogContext['closeDeleteProductClaimDialog'] =
    useCallback(() => {
      setDeleteProductClaimDialogProps(undefined);
      setIsOpenDeleteProductClaimDialog(false);
    }, []);

  //  Create Admin Dialog
  const [isOpenCreateAdminDialog, setIsOpenCreateAdminDialog] =
    useState<boolean>(false);
  const openCreateAdminDialog: V2DialogContext['openCreateAdminDialog'] =
    useCallback(() => {
      setIsOpenCreateAdminDialog(true);
    }, []);
  const closeCreateAdminDialog: V2DialogContext['closeCreateAdminDialog'] =
    useCallback(() => {
      setIsOpenCreateAdminDialog(false);
    }, []);

  //  Detail Admin Dialog
  const [isOpenDetailAdminDialog, setIsOpenDetailAdminDialog] =
    useState<boolean>(false);
  const [detailAdminDialogProps, setDetailAdminDialogProps] =
    useState<Omit<DetailAdminDialogProps, 'dialog'>>();

  const openDetailAdminDialog: V2DialogContext['openDetailAdminDialog'] =
    useCallback(props => {
      setDetailAdminDialogProps(props);
      setIsOpenDetailAdminDialog(true);
    }, []);

  const closeDetailAdminDialog: V2DialogContext['closeDetailAdminDialog'] =
    useCallback(() => {
      setDetailAdminDialogProps(undefined);
      setIsOpenDetailAdminDialog(false);
    }, []);

  //  Edit Admin Dialog
  const [isOpenEditAdminDialog, setIsOpenEditAdminDialog] =
    useState<boolean>(false);
  const [editAdminDialogProps, setEditAdminDialogProps] =
    useState<Omit<EditAdminDialogProps, 'dialog'>>();

  const openEditAdminDialog: V2DialogContext['openEditAdminDialog'] =
    useCallback(props => {
      setEditAdminDialogProps(props);
      setIsOpenEditAdminDialog(true);
    }, []);

  const closeEditAdminDialog: V2DialogContext['closeEditAdminDialog'] =
    useCallback(() => {
      setEditAdminDialogProps(undefined);
      setIsOpenEditAdminDialog(false);
    }, []);

  //  Delete Admin Dialog
  const [isOpenDeleteAdminDialog, setIsOpenDeleteAdminDialog] =
    useState<boolean>(false);
  const [deleteAdminDialogProps, setDeleteAdminDialogProps] =
    useState<Omit<DeleteAdminDialogProps, 'dialog'>>();

  const openDeleteAdminDialog: V2DialogContext['openDeleteAdminDialog'] =
    useCallback(props => {
      setDeleteAdminDialogProps(props);
      setIsOpenDeleteAdminDialog(true);
    }, []);

  const closeDeleteAdminDialog: V2DialogContext['closeDeleteAdminDialog'] =
    useCallback(() => {
      setDeleteAdminDialogProps(undefined);
      setIsOpenDeleteAdminDialog(false);
    }, []);

  //  Change Admin Password Dialog
  const [isOpenChangeAdminPasswordDialog, setIsOpenChangeAdminPasswordDialog] =
    useState<boolean>(false);
  const [changeAdminPasswordDialogProps, setChangeAdminPasswordDialogProps] =
    useState<Omit<ChangeAdminPasswordDialogProps, 'dialog'>>();

  const openChangeAdminPasswordDialog: V2DialogContext['openChangeAdminPasswordDialog'] =
    useCallback(props => {
      setChangeAdminPasswordDialogProps(props);
      setIsOpenChangeAdminPasswordDialog(true);
    }, []);

  const closeChangeAdminPasswordDialog: V2DialogContext['closeChangeAdminPasswordDialog'] =
    useCallback(() => {
      setChangeAdminPasswordDialogProps(undefined);
      setIsOpenChangeAdminPasswordDialog(false);
    }, []);

  //  Create Role Dialog
  const [isOpenCreateRoleDialog, setIsOpenCreateRoleDialog] =
    useState<boolean>(false);
  const openCreateRoleDialog: V2DialogContext['openCreateRoleDialog'] =
    useCallback(() => {
      setIsOpenCreateRoleDialog(true);
    }, []);
  const closeCreateRoleDialog: V2DialogContext['closeCreateRoleDialog'] =
    useCallback(() => {
      setIsOpenCreateRoleDialog(false);
    }, []);

  //  Detail Role Dialog
  const [isOpenDetailRoleDialog, setIsOpenDetailRoleDialog] =
    useState<boolean>(false);
  const [detailRoleDialogProps, setDetailRoleDialogProps] =
    useState<Omit<DetailRoleDialogProps, 'dialog'>>();

  const openDetailRoleDialog: V2DialogContext['openDetailRoleDialog'] =
    useCallback(props => {
      setDetailRoleDialogProps(props);
      setIsOpenDetailRoleDialog(true);
    }, []);

  const closeDetailRoleDialog: V2DialogContext['closeDetailRoleDialog'] =
    useCallback(() => {
      setDetailRoleDialogProps(undefined);
      setIsOpenDetailRoleDialog(false);
    }, []);

  //  Edit Role Dialog
  const [isOpenEditRoleDialog, setIsOpenEditRoleDialog] =
    useState<boolean>(false);
  const [editRoleDialogProps, setEditRoleDialogProps] =
    useState<Omit<EditRoleDialogProps, 'dialog'>>();

  const openEditRoleDialog: V2DialogContext['openEditRoleDialog'] = useCallback(
    props => {
      setEditRoleDialogProps(props);
      setIsOpenEditRoleDialog(true);
    },
    []
  );

  const closeEditRoleDialog: V2DialogContext['closeEditRoleDialog'] =
    useCallback(() => {
      setEditRoleDialogProps(undefined);
      setIsOpenEditRoleDialog(false);
    }, []);

  //  Delete Role Dialog
  const [isOpenDeleteRoleDialog, setIsOpenDeleteRoleDialog] =
    useState<boolean>(false);
  const [deleteRoleDialogProps, setDeleteRoleDialogProps] =
    useState<Omit<DeleteRoleDialogProps, 'dialog'>>();

  const openDeleteRoleDialog: V2DialogContext['openDeleteRoleDialog'] =
    useCallback(props => {
      setDeleteRoleDialogProps(props);
      setIsOpenDeleteRoleDialog(true);
    }, []);

  const closeDeleteRoleDialog: V2DialogContext['closeDeleteRoleDialog'] =
    useCallback(() => {
      setDeleteRoleDialogProps(undefined);
      setIsOpenDeleteRoleDialog(false);
    }, []);

  //  Detail Notification Delivery Dialog
  const [
    isOpenDetailNotificationDeliveryDialog,
    setIsOpenDetailNotificationDeliveryDialog,
  ] = useState<boolean>(false);
  const [
    detailNotificationDeliveryDialogProps,
    setDetailNotificationDeliveryDialogProps,
  ] = useState<Omit<DetailNotificationDeliveryDialogProps, 'dialog'>>();

  const openDetailNotificationDeliveryDialog: V2DialogContext['openDetailNotificationDeliveryDialog'] =
    useCallback(props => {
      setDetailNotificationDeliveryDialogProps(props);
      setIsOpenDetailNotificationDeliveryDialog(true);
    }, []);

  const closeDetailNotificationDeliveryDialog: V2DialogContext['closeDetailNotificationDeliveryDialog'] =
    useCallback(() => {
      setDetailNotificationDeliveryDialogProps(undefined);
      setIsOpenDetailNotificationDeliveryDialog(false);
    }, []);

  const value = useMemo<V2DialogContext>(
    () => ({
      openCreateVoucherDialog,
      closeCreateVoucherDialog,
      openUpdateVoucherDialog,
      closeUpdateVoucherDialog,
      openDeleteVoucherDialog,
      closeDeleteVoucherDialog,
      openDetailVoucherDialog,
      closeDetailVoucherDialog,
      openDetailUserDialog,
      closeDetailUserDialog,
      openEditUserDialog,
      closeEditUserDialog,
      openDeleteUserDialog,
      closeDeleteUserDialog,
      openDetailOrderDialog,
      closeDetailOrderDialog,
      openSetOrderPaidDialog,
      closeSetOrderPaidDialog,
      openDetailWithdrawDialog,
      closeDetailWithdrawDialog,
      openUploadTransferProofDialog,
      closeUploadTransferProofDialog,
      openCancelWithdrawDialog,
      closeCancelWithdrawDialog,
      openDetailSessionDialog,
      closeDetailSessionDialog,
      openDeleteProductDialog,
      closeDeleteProductDialog,
      openPublishProductDialog,
      closePublishProductDialog,
      openArchiveProductDialog,
      closeArchiveProductDialog,
      openPrivateProductDialog,
      closePrivateProductDialog,
      openDeleteQuestionBankDialog,
      closeDeleteQuestionBankDialog,
      openCreateProductClaimDialog,
      closeCreateProductClaimDialog,
      openUpdateProductClaimDialog,
      closeUpdateProductClaimDialog,
      openDeleteProductClaimDialog,
      closeDeleteProductClaimDialog,
      openCreateAdminDialog,
      closeCreateAdminDialog,
      openDetailAdminDialog,
      closeDetailAdminDialog,
      openEditAdminDialog,
      closeEditAdminDialog,
      openDeleteAdminDialog,
      closeDeleteAdminDialog,
      openChangeAdminPasswordDialog,
      closeChangeAdminPasswordDialog,
      openCreateRoleDialog,
      closeCreateRoleDialog,
      openDetailRoleDialog,
      closeDetailRoleDialog,
      openEditRoleDialog,
      closeEditRoleDialog,
      openDeleteRoleDialog,
      closeDeleteRoleDialog,
      openDetailNotificationDeliveryDialog,
      closeDetailNotificationDeliveryDialog,
    }),
    [
      openCreateVoucherDialog,
      closeCreateVoucherDialog,
      openUpdateVoucherDialog,
      closeUpdateVoucherDialog,
      openDeleteVoucherDialog,
      closeDeleteVoucherDialog,
      openDetailVoucherDialog,
      closeDetailVoucherDialog,
      openDetailUserDialog,
      closeDetailUserDialog,
      openEditUserDialog,
      closeEditUserDialog,
      openDeleteUserDialog,
      closeDeleteUserDialog,
      openDetailOrderDialog,
      closeDetailOrderDialog,
      openSetOrderPaidDialog,
      closeSetOrderPaidDialog,
      openDetailWithdrawDialog,
      closeDetailWithdrawDialog,
      openUploadTransferProofDialog,
      closeUploadTransferProofDialog,
      openCancelWithdrawDialog,
      closeCancelWithdrawDialog,
      openDetailSessionDialog,
      closeDetailSessionDialog,
      openDeleteProductDialog,
      closeDeleteProductDialog,
      openPublishProductDialog,
      closePublishProductDialog,
      openArchiveProductDialog,
      closeArchiveProductDialog,
      openPrivateProductDialog,
      closePrivateProductDialog,
      openDeleteQuestionBankDialog,
      closeDeleteQuestionBankDialog,
      openCreateProductClaimDialog,
      closeCreateProductClaimDialog,
      openUpdateProductClaimDialog,
      closeUpdateProductClaimDialog,
      openDeleteProductClaimDialog,
      closeDeleteProductClaimDialog,
      openCreateAdminDialog,
      closeCreateAdminDialog,
      openDetailAdminDialog,
      closeDetailAdminDialog,
      openEditAdminDialog,
      closeEditAdminDialog,
      openDeleteAdminDialog,
      closeDeleteAdminDialog,
      openChangeAdminPasswordDialog,
      closeChangeAdminPasswordDialog,
      openCreateRoleDialog,
      closeCreateRoleDialog,
      openDetailRoleDialog,
      closeDetailRoleDialog,
      openEditRoleDialog,
      closeEditRoleDialog,
      openDeleteRoleDialog,
      closeDeleteRoleDialog,
      openDetailNotificationDeliveryDialog,
      closeDetailNotificationDeliveryDialog,
    ]
  );

  return (
    <V2dialogContext.Provider value={value}>
      {children}

      {/* Vouchers */}
      <CreateVoucherDialog
        dialog={{
          open: isOpenCreateVoucherDialog,
        }}
      />
      {updateVoucherDialogProps && (
        <UpdateVoucherDialog
          {...updateVoucherDialogProps}
          dialog={{ open: isOpenUpdateVoucherDialog }}
        />
      )}
      {deleteVoucherDialogProps && (
        <DeleteVoucherDialog
          {...deleteVoucherDialogProps}
          dialog={{ open: isOpenDeleteVoucherDialog }}
        />
      )}
      {detailVoucherDialogProps && (
        <DetailVoucherDialog
          {...detailVoucherDialogProps}
          dialog={{ open: isOpenDetailVoucherDialog }}
        />
      )}

      {/* Users */}
      {detailUserDialogProps && (
        <DetailUserDialog
          {...detailUserDialogProps}
          dialog={{ open: isOpenDetailUserDialog }}
        />
      )}
      {editUserDialogProps && (
        <EditUserDialog
          {...editUserDialogProps}
          dialog={{ open: isOpenEditUserDialog }}
        />
      )}
      {deleteUserDialogProps && (
        <DeleteUserDialog
          {...deleteUserDialogProps}
          dialog={{ open: isOpenDeleteUserDialog }}
        />
      )}

      {/* Orders */}
      {detailOrderDialogProps && (
        <DetailOrderDialog
          {...detailOrderDialogProps}
          dialog={{ open: isOpenDetailOrderDialog }}
        />
      )}
      {setOrderPaidDialogProps && (
        <SetOrderPaidDialog
          {...setOrderPaidDialogProps}
          dialog={{ open: isOpenSetOrderPaidDialog }}
        />
      )}

      {/* Withdraws */}
      {detailWithdrawDialogProps && (
        <DetailWithdrawDialog
          {...detailWithdrawDialogProps}
          dialog={{ open: isOpenDetailWithdrawDialog }}
        />
      )}
      {uploadTransferProofDialogProps && (
        <UploadTransferProofDialog
          {...uploadTransferProofDialogProps}
          dialog={{ open: isOpenUploadTransferProofDialog }}
        />
      )}
      {cancelWithdrawDialogProps && (
        <CancelWithdrawDialog
          {...cancelWithdrawDialogProps}
          dialog={{ open: isOpenCancelWithdrawDialog }}
        />
      )}

      {/* Sessions */}
      {detailSessionDialogProps && (
        <DetailSessionDialog
          {...detailSessionDialogProps}
          dialog={{ open: isOpenDetailSessionDialog }}
        />
      )}

      {/* Products */}
      {deleteProductDialogProps && (
        <DeleteProductDialog
          {...deleteProductDialogProps}
          dialog={{ open: isOpenDeleteProductDialog }}
        />
      )}
      {publishProductDialogProps && (
        <PublishProductDialog
          {...publishProductDialogProps}
          dialog={{ open: isOpenPublishProductDialog }}
        />
      )}
      {archiveProductDialogProps && (
        <ArchiveProductDialog
          {...archiveProductDialogProps}
          dialog={{ open: isOpenArchiveProductDialog }}
        />
      )}
      {privateProductDialogProps && (
        <PrivateProductDialog
          {...privateProductDialogProps}
          dialog={{ open: isOpenPrivateProductDialog }}
        />
      )}

      {/* Question Banks */}
      {deleteQuestionBankDialogProps && (
        <DeleteQuestionBankDialog
          {...deleteQuestionBankDialogProps}
          dialog={{ open: isOpenDeleteQuestionBankDialog }}
        />
      )}

      {/* Klaim Produk */}
      <CreateProductClaimDialog
        dialog={{
          open: isOpenCreateProductClaimDialog,
        }}
      />
      {updateProductClaimDialogProps && (
        <UpdateProductClaimDialog
          {...updateProductClaimDialogProps}
          dialog={{ open: isOpenUpdateProductClaimDialog }}
        />
      )}
      {deleteProductClaimDialogProps && (
        <DeleteProductClaimDialog
          {...deleteProductClaimDialogProps}
          dialog={{ open: isOpenDeleteProductClaimDialog }}
        />
      )}

      {/* Admins */}
      <CreateAdminDialog
        dialog={{
          open: isOpenCreateAdminDialog,
        }}
      />
      {detailAdminDialogProps && (
        <DetailAdminDialog
          {...detailAdminDialogProps}
          dialog={{ open: isOpenDetailAdminDialog }}
        />
      )}
      {editAdminDialogProps && (
        <EditAdminDialog
          {...editAdminDialogProps}
          dialog={{ open: isOpenEditAdminDialog }}
        />
      )}
      {deleteAdminDialogProps && (
        <DeleteAdminDialog
          {...deleteAdminDialogProps}
          dialog={{ open: isOpenDeleteAdminDialog }}
        />
      )}
      {changeAdminPasswordDialogProps && (
        <ChangeAdminPasswordDialog
          {...changeAdminPasswordDialogProps}
          dialog={{ open: isOpenChangeAdminPasswordDialog }}
        />
      )}

      {/* Roles */}
      <CreateRoleDialog
        dialog={{
          open: isOpenCreateRoleDialog,
        }}
      />
      {detailRoleDialogProps && (
        <DetailRoleDialog
          {...detailRoleDialogProps}
          dialog={{ open: isOpenDetailRoleDialog }}
        />
      )}
      {editRoleDialogProps && (
        <EditRoleDialog
          {...editRoleDialogProps}
          dialog={{ open: isOpenEditRoleDialog }}
        />
      )}
      {deleteRoleDialogProps && (
        <DeleteRoleDialog
          {...deleteRoleDialogProps}
          dialog={{ open: isOpenDeleteRoleDialog }}
        />
      )}

      {/* Notification Deliveries */}
      {detailNotificationDeliveryDialogProps && (
        <DetailNotificationDeliveryDialog
          {...detailNotificationDeliveryDialogProps}
          dialog={{ open: isOpenDetailNotificationDeliveryDialog }}
        />
      )}
    </V2dialogContext.Provider>
  );
}
